/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	IActionResponse
} from '@shared/interfaces/workflow/action-response.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';

@Component({
	selector: 'make-payment',
	templateUrl: './make-payment.component.html'
})

/**
 * A component representing a wizard step for making a policy term payment.
 *
 * @export
 * @class MakePaymentComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class MakePaymentComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the make payment component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes on entity creation.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service used in this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @memberof MakePaymentComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<DynamicWizardComponent, IWizardContext>}
	 * @memberof MakePaymentComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof MakePaymentComponent
	 */
	public currentData: any;

	/**
	 * Gets or sets the PolicyTerm entity type to be updated.
	 *
	 * @type {IEntityType}
	 * @memberof MakePaymentComponent
	 */
	public policyTermEntityType: IEntityType;

	/**
	 * Gets or sets the PolicyTerm entity instance associated to the current
	 * data.
	 *
	 * @type {IEntityInstance}
	 * @memberof MakePaymentComponent
	 */
	public policyTermEntityInstance: IEntityInstance;

	/**
	 * Gets the workflow action being executed to process the payment.
	 *
	 * @type {string}
	 * @memberof MakePaymentComponent
	 */
	private readonly workflowActionName: string =
		'MakePayment';

	/**
	 * Gets the name of the next wizard step.
	 *
	 * @type {string}
	 * @memberof MakePaymentComponent
	 */
	private readonly nextStepName: string =
		'Receipt';

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof MakePaymentComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.currentData =
			this.context.source.activeMenuItem.currentData.data;

		const policyTermId = this.currentData.id;

		this.policyTermEntityType =
			await this.entityTypeApiService
				.getSingleQueryResult(
					`Name eq '${this.currentData.entityType}'`,
					AppConstants.empty);

		this.policyTermEntityInstance =
			await this.getPolicyTerm(
				this.policyTermEntityType.group,
				policyTermId);

		this.context.source.addToNext(this.makePayment.bind(this));

		this.context.source.activeWizardStep.alwaysVerify = true;
		this.context.source.wizardStepLoading = false;
		this.context.source.changeDetectorReference.detectChanges();

		await this.context.source.validStepChanged(true);
	}

	/**
	 * This will send the make payment event and navigate to the new
	 * entity.
	 *
	 * @async
	 * @memberof MakePaymentComponent
	 */
	public async makePayment(): Promise<void>
	{
		const queryString: string =
			`?paymentMethod=${this.currentData.paymentMethod.resourceIdentifier}`
				+ `&amount=${this.currentData.paymentAmount}`;

		const result: any =
			await this.makePaymentExecution(
				this.policyTermEntityType,
				this.policyTermEntityInstance,
				queryString);

		if (result.value?.success)
		{
			this.context
				.source
				.stepTo(
					this.nextStepName);

			this.context.source.changeDetectorReference.detectChanges();
		}
		else
		{
			this.navigateToPolicyTermDashboard(
				this.policyTermEntityInstance.id,
				this.policyTermEntityType.group);
		}
	}

	/**
	 * Gets the PolicyTerm instance data associated to the current id from the
	 * Payment Wizard data.
	 *
	 * @async
	 * @param {IEntityType} policyTermEntityTypeGroupName
	 * The PolicyTerm entity type group name.
	 * @param {number} policyTermId
	 * The PolicyTerm entity instance id.
	 * @memberof MakePaymentComponent
	 */
	public async getPolicyTerm(
		policyTermEntityTypeGroupName: string,
		policyTermId: number): Promise<IEntityInstance>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			policyTermEntityTypeGroupName;

		const policyTermInstance: IEntityInstance =
			await this.entityInstanceApiService
				.get(policyTermId);

		return policyTermInstance;
	}

	/**
	 * Executes the make payment process, which executes the MakePayment
	 * workflow service with the collected data from the make payment wizard,
	 * then creates the payment information.
	 *
	 * @async
	 * @param {IEntityType} policyTermEntityType
	 * The PolicyTerm entity type.
	 * @param {IEntityInstance} policyTermInstanceData
	 * The PolicyTerm entity instance data to be updated.
	 * @param {string} queryString
	 * The extra parameters sent as query string parameters.
	 * @returns {Promise<any>}
	 * Returns the result of the workflow action execution.
	 * @memberof MakePaymentComponent
	 */
	private async makePaymentExecution(
		policyTermEntityType: IEntityType,
		policyTermInstanceData: IEntityInstance,
		queryString: string): Promise<any>
	{
		setTimeout(
			() =>
			{
				this.context.source.wizardStepLoading = true;
				this.context.source.changeDetectorReference.detectChanges();
			});

		const result: any =
			await this.activityService.handleActivity(
				new Activity<any>(
					new Promise(
						async (
							resolve: any,
							reject: any) =>
						{
							try
							{
								const actionResponse: any =
									await this.executeWorkflow(
										this.workflowActionName,
										policyTermEntityType.group,
										policyTermInstanceData,
										queryString);

								this.context.source.addOrUpdateStepData(
									<any>
									{
										paymentResult:
											actionResponse.body.value
									});

								resolve(actionResponse.body);
							}
							catch (error)
							{
								reject(error);
							}
						}),
					'<strong>Processing payment</strong>',
					'<strong>Payment processed</strong>',
					'Payment processed successfully.',
					'Payment failed to process.'));

		return result;
	}

	/**
	 * Executes a given workflow action.
	 *
	 * @async
	 * @param {string} workflowActionName
	 * The name of the workflow action to execute
	 * @param {string} policyTermEntityTypeGroupName
	 * The PolicyTerm entity type group name.
	 * @param {IEntityInstance} policyTermInstanceData
	 * The PolicyTerm entity instance data.
	 * @param {string} queryString
	 * The extra parameters sent as query string parameters.
	 * @returns {Promise<IActionResponse>}
	 * The IActionResponse response from the workflow action
	 * execution.
	 * @memberof MakePaymentComponent
	 */
	private async executeWorkflow(
		workflowActionName: string,
		policyTermEntityTypeGroupName: string,
		policyTermInstanceData: IEntityInstance,
		queryString: string): Promise<IActionResponse>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			policyTermEntityTypeGroupName;

		const actionResponse: IActionResponse =
			await this.entityInstanceApiService
				.executeAction(
					policyTermInstanceData.id,
					workflowActionName,
					null,
					queryString);

		return actionResponse;
	}

	/**
	 * This will navigate to the policy term dashboard using the policy term id
	 * provided.
	 *
	 * @async
	 * @param {number} entityId
	 * The policy term entity id to navigate.
	 * @param {string} group
	 * The entity group associated to the navigation.
	 * @memberof MakePaymentComponent
	 */
	private async navigateToPolicyTermDashboard(
		entityId: number,
		group: string)
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				group,
				AppConstants.viewTypes.edit,
				entityId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}
}